// Fields the SKU catalog can supply to an order item. Unlike dimensions, these
// are dropdowns a shipper may deliberately change for one order, so a catalog
// value only ever fills a gap — it never overwrites a set value.
const CATALOG_FIELDS = ['item_type_details', 'assembly_type', 'rate_class'];

// description is pick-only: the gap-fill effect re-runs whenever item type or assembly type
// change, so gap-filling it would undo a deliberate clear on the next unrelated edit.
const PICK_FIELDS = [...CATALOG_FIELDS, 'description'];

// dimensionOverrides mirrors the catalog's `cubes`; the rename to cubes_per_unit is the hook's job.
const DIMENSION_FIELDS = ['length', 'width', 'height', 'weight', 'cubes'];
const ITEM_DIMENSION_KEYS = { cubes: 'cubes_per_unit' };

const isAbsent = (value) => value === null || value === undefined || value === '';

export const skuCatalogPatch = (item, cached, { overwrite = false } = {}) => {
    const patch = (overwrite ? PICK_FIELDS : CATALOG_FIELDS).reduce((acc, field) => {
        // rate_class already went through the ingest override for a persisted item; only a SKU pick may touch it.
        if (!overwrite && field === 'rate_class' && item?.item_id) {
            return acc;
        }
        if (isAbsent(cached?.[field])) {
            return acc;
        }
        if (!overwrite && !isAbsent(item?.[field])) {
            return acc;
        }
        return { ...acc, [field]: cached[field] };
    }, {});
    // rate_class is not editable on the item, so picking an unclassed SKU must clear the old class.
    return overwrite && isAbsent(cached?.rate_class) && !isAbsent(item?.rate_class)
        ? { ...patch, rate_class: null }
        : patch;
};

export const skuDimensionOverrides = (prev, cached) =>
    DIMENSION_FIELDS.reduce(
        (next, field) => ({
            ...next,
            [field]: isAbsent(cached?.[field]) ? prev?.[field] : cached[field],
        }),
        {}
    );

// Only what the catalog filled — marking all five would surface a "weight required" error the
// moment someone picks a SKU with no weight.
export const dirtyDimensionKeys = (cached) =>
    DIMENSION_FIELDS.filter((field) => !isAbsent(cached?.[field])).map((field) => ITEM_DIMENSION_KEYS[field] || field);

// CSV import columns the SKU catalog can fill, by CSV option key.
const CSV_CATALOG_FIELDS = {
    length: 'length',
    width: 'width',
    height: 'height',
    weight: 'weight',
    desc: 'description',
    item_service_time: 'service_time',
    item_type_deets: 'item_type_details',
    assembly_type: 'assembly_type',
};

// Columns the CSV parse leaves blank until the catalog has filled what it can, so a column default
// (Items Weight defaults to 12) never passes for a value the shipper entered.
export const CSV_CATALOG_KEYS = [...Object.keys(CSV_CATALOG_FIELDS), 'itemCubes'];

const SIZE_KEYS = ['length', 'width', 'height', 'itemCubes'];
const ZERO_BLANK_KEYS = [...SIZE_KEYS, 'weight'];

// parseInt turns an unreadable cell ("N/A") into NaN, which is as empty as a blank one. So is a 0 size or
// weight, since convertItemFields treats 0 as missing and falls back to its defaults. 0 is a real service time.
const isBlankCell = (key, value) =>
    isAbsent(value) || Number.isNaN(value) || (value === 0 && ZERO_BLANK_KEYS.includes(key));

// Fills a parsed CSV row's blank cells from its SKU's catalog row; cells the file populated always win.
// Cubic Feet only stands in when the row has no size at all, and household reads that column as the
// line's total, so the catalog's per-unit cubes scale by quantity.
export const fillCsvRowFromSku = (row, cached, { freightType } = {}) => {
    if (!cached) {
        return row;
    }

    const filled = Object.entries(CSV_CATALOG_FIELDS).reduce(
        (next, [key, field]) =>
            isBlankCell(key, next[key]) && !isAbsent(cached[field]) ? { ...next, [key]: cached[field] } : next,
        row
    );

    const hasSize = SIZE_KEYS.some((key) => !isBlankCell(key, filled[key]));
    if (hasSize || isAbsent(cached.cubes)) {
        return filled;
    }

    const qty = Math.abs(parseInt(filled.qty)) || 1;
    return { ...filled, itemCubes: freightType === 'household' ? cached.cubes * qty : cached.cubes };
};
